import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Star, Camera, X, Loader2, CheckCircle2, Clock, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { resizeImageToDataUrl } from '../utils/imageResize';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Public magic-link review page — /review/:token
 * Customers land here from the 7-day post-trip email. No account needed.
 */
export default function ReviewRequestPage() {
  const { token } = useParams();
  const [phase, setPhase] = useState('loading'); // loading | form | success | expired | invalid
  const [info, setInfo] = useState(null);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/v2/public/review-requests/${token}`);
        const data = await res.json();
        if (!alive) return;
        if (!data.valid) {
          setPhase(data.reason === 'expired' ? 'expired' : 'invalid');
          return;
        }
        setInfo(data);
        if (data.existingReview) {
          setRating(data.existingReview.rating || 0);
          setTitle(data.existingReview.title || '');
          setComment(data.existingReview.comment || '');
        }
        setPhase('form');
      } catch {
        if (alive) setPhase('invalid');
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const maxPhotos = info?.maxPhotos || 3;
  const existingPhotoCount = info?.existingReview?.photoCount || 0;
  const remainingSlots = Math.max(0, maxPhotos - existingPhotoCount);

  const addPhotos = async (files) => {
    const remaining = remainingSlots - photos.length;
    const picked = Array.from(files || []).slice(0, remaining);
    for (const f of picked) {
      try {
        const dataUrl = await resizeImageToDataUrl(f, 1200, 0.8);
        setPhotos((p) => (p.length < remainingSlots ? [...p, dataUrl] : p));
      } catch (e) {
        toast.error(e.message);
      }
    }
  };

  const submit = async () => {
    if (!rating) { toast.error('Pick a star rating first'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/v2/public/review-requests/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating, title: title || null, comment: comment || null, photos }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Failed to submit review');
      setPhase('success');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const tripDate = info?.bookingDate
    ? new Date(info.bookingDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
    : '';

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="review-request-page">
      <div className="flex-1 flex items-start sm:items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">{children}</div>
      </div>
      <p className="pb-6 text-center text-xs text-gray-500">
        Powered by <Link to="/" className="font-semibold text-gray-600 hover:text-gray-800">BookingFlows</Link>
      </p>
    </div>
  );

  if (phase === 'loading') {
    return (
      <Shell>
        <div className="flex justify-center py-24">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      </Shell>
    );
  }

  if (phase === 'expired' || phase === 'invalid') {
    const expired = phase === 'expired';
    return (
      <Shell>
        <div className="bg-white rounded-3xl shadow-sm p-8 text-center" data-testid={expired ? 'review-request-expired' : 'review-request-invalid'}>
          <div className="w-14 h-14 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-4">
            {expired ? <Clock className="w-7 h-7 text-gray-400" /> : <ShieldAlert className="w-7 h-7 text-gray-400" />}
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            {expired ? 'This review link has expired' : "This review link isn't valid"}
          </h1>
          <p className="text-sm text-gray-500 mb-6">
            {expired
              ? 'Review links are valid for 14 days after we send them. You can still browse and book more experiences.'
              : 'The link may be incomplete — try opening it again from your email.'}
          </p>
          <Button asChild variant="outline" className="rounded-full">
            <Link to="/marketplace" data-testid="review-request-marketplace-link">Explore experiences</Link>
          </Button>
        </div>
      </Shell>
    );
  }

  if (phase === 'success') {
    return (
      <Shell>
        <div className="bg-white rounded-3xl shadow-sm p-8 text-center" data-testid="review-request-success">
          <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-4">
            <CheckCircle2 className="w-7 h-7 text-emerald-500" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            Thanks for sharing{info?.customerFirstName ? `, ${info.customerFirstName}` : ''}!
          </h1>
          <p className="text-sm text-gray-500 mb-6">
            Your review is live{photos.length > 0 ? ' and your photos have been added to the experience gallery' : ''}.
            {info?.operator?.name ? ` The ${info.operator.name} crew appreciates it.` : ''}
          </p>
          {info?.serviceId && (
            <Button asChild className="rounded-full bg-blue-600 hover:bg-blue-700">
              <Link to={`/experience/${info.serviceId}`} data-testid="review-request-view-experience-btn">
                See your review on the page
              </Link>
            </Button>
          )}
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
        {/* Operator header */}
        <div className="px-6 pt-7 pb-5 text-center border-b border-gray-100">
          {info?.operator?.logoUrl ? (
            <img
              src={info.operator.logoUrl}
              alt={info.operator.name}
              className="w-14 h-14 mx-auto rounded-2xl object-contain bg-gray-50 p-1 mb-3"
              data-testid="review-request-operator-logo"
            />
          ) : (
            <div className="w-14 h-14 mx-auto rounded-2xl bg-blue-600 flex items-center justify-center mb-3">
              <span className="text-white font-bold text-xl">{info?.operator?.name?.charAt(0) || '?'}</span>
            </div>
          )}
          <h1 className="text-xl font-bold text-gray-900" data-testid="review-request-heading">
            {info?.alreadyReviewed ? 'Update your review' : `How was your trip${info?.customerFirstName ? `, ${info.customerFirstName}` : ''}?`}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {info?.serviceName} · {info?.operator?.name}
          </p>
          {tripDate && <p className="text-xs text-gray-400 mt-0.5">{tripDate}</p>}
        </div>

        <div className="p-6 space-y-5">
          {/* Stars */}
          <div className="flex justify-center gap-1.5" data-testid="review-request-stars">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                data-testid={`review-request-star-${n}`}
                onMouseEnter={() => setHoverRating(n)}
                onMouseLeave={() => setHoverRating(0)}
                onClick={() => setRating(n)}
                className="p-1 transition-transform hover:scale-110"
              >
                <Star className={`w-9 h-9 ${(hoverRating || rating) >= n ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} />
              </button>
            ))}
          </div>

          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title (optional)"
            maxLength={120}
            className="rounded-xl"
            data-testid="review-request-title-input"
          />
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="How was your experience?"
            rows={4}
            maxLength={2000}
            className="rounded-xl"
            data-testid="review-request-comment-input"
          />

          {/* Photos */}
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              {photos.map((p, i) => (
                <div key={i} className="relative">
                  <img src={p} alt={`upload ${i + 1}`} className="w-16 h-16 rounded-xl object-cover" />
                  <button
                    type="button"
                    onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-gray-900 text-white flex items-center justify-center"
                    data-testid={`review-request-photo-remove-${i}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {photos.length < remainingSlots && (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="w-16 h-16 rounded-xl bg-gray-50 hover:bg-gray-100 flex flex-col items-center justify-center text-gray-400 transition-colors"
                  data-testid="review-request-add-photo-btn"
                >
                  <Camera className="w-5 h-5" />
                  <span className="text-[10px] mt-0.5">Photo</span>
                </button>
              )}
            </div>
            <p className="text-[11px] text-gray-500 mt-1.5" data-testid="review-request-photo-helper">
              {remainingSlots === 0
                ? `You've already added ${existingPhotoCount} photos — the maximum of ${maxPhotos}`
                : existingPhotoCount > 0
                  ? `You've already added ${existingPhotoCount} — add up to ${remainingSlots} more`
                  : `Add up to ${maxPhotos} photos — they'll appear in the experience gallery`}
            </p>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => { addPhotos(e.target.files); e.target.value = ''; }}
            />
          </div>

          <Button
            onClick={submit}
            disabled={submitting}
            className="w-full h-11 bg-blue-600 hover:bg-blue-700 rounded-xl font-semibold"
            data-testid="review-request-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (info?.alreadyReviewed ? 'Update review' : 'Submit review')}
          </Button>
          <p className="text-center text-[11px] text-gray-500">No account needed — verified trip review</p>
        </div>
      </div>
    </Shell>
  );
}
